import { isFilled } from "@prismicio/client";
import CollectionSlider from "@/components/molecules/collection-slider";
import InspirationImage from "@/components/molecules/inspiration-image";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { InspirationDocument } from "@/prismicio-types";
import Link from "next/link";

type InspirationCollectionProps = {
  inspirations: InspirationDocument[];
  locale: Locales;
  variant?: "default" | "table";
  className?: string;
};

const InspirationCollection = ({
  inspirations,
  locale,
  variant = "default",
  className,
}: InspirationCollectionProps) => {
  if (!inspirations || inspirations.length === 0) return null;

  const getInspirationLink = (doc: InspirationDocument) => {
    let slug: string | null = null;

    const validCategory = doc.data.categories?.find(
      (cat) => isFilled.contentRelationship(cat.inspirationCategory) && "slug" in cat.inspirationCategory
    );

    if (
      validCategory &&
      isFilled.contentRelationship(validCategory.inspirationCategory) &&
      "slug" in validCategory.inspirationCategory
    ) {
      slug = validCategory.inspirationCategory.slug || null;
    }

    if (!slug) return "#";

    return `${getPermalink(locale, "inspiration_archive")}/${slug}#${doc.uid}`;
  };

  const renderImage = (doc: InspirationDocument, className?: string) => (
    <InspirationImage
      locale={locale}
      image={doc.data.featured_image?.url || ""}
      imageAlt={doc.data.featured_image?.alt || ""}
      link={getInspirationLink(doc)}
      variant="default"
      className={className}
      category={doc.data.title || ""}
    />
  );

  const renderContent = () => {
    const itemCount = inspirations.length;

    if (itemCount === 1) {
      return <div className="md:col-span-2">{renderImage(inspirations[0])}</div>;
    }

    if (itemCount === 2) {
      return (
        <>
          {renderImage(inspirations[0])}
          {renderImage(inspirations[1])}
        </>
      );
    }

    if (itemCount === 3) {
      return (
        <>
          {renderImage(inspirations[0])}
          <div className="grid grid-cols-1 gap-5">
            {renderImage(inspirations[1], "md:aspect-[unset]")}
            {renderImage(inspirations[2], "md:aspect-[unset]")}
          </div>
        </>
      );
    }

    const sliderItems = inspirations.slice(2).map((item) => ({
      image: item.data.featured_image?.url || "",
      tag: item.data.title || "",
      link: getInspirationLink(item),
    }));

    return (
      <>
        <CollectionSlider locale={locale} collections={sliderItems} />
        <div className="grid grid-cols-1 gap-5">
          {renderImage(inspirations[0], "md:aspect-[unset]")}
          {renderImage(inspirations[1], "md:aspect-[unset]")}
        </div>
      </>
    );
  };

  return (
    <section className={cn("container grid grid-cols-1 gap-5 px-4 py-6 md:grid-cols-2 md:px-7.5", className)}>
      <header
        className={cn(
          "flex flex-col items-center justify-between gap-3 md:col-span-2 md:flex-row",
          variant === "table" && "md:flex-col"
        )}
      >
        {variant === "default" ? (
          <>
            <h2 className="font-cabinet-grotesk text-center text-3xl font-medium">{__("Our inspirations", locale)}</h2>
            <Link
              className="text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none md:uppercase"
              href={getPermalink(locale, "inspiration")}
            >
              {__("See all inspirations", locale)}
            </Link>
          </>
        ) : (
          <h2 className="font-cabinet-grotesk text-center">{__("Discover other “Tables”", locale)}</h2>
        )}
      </header>

      {renderContent()}
    </section>
  );
};

export default InspirationCollection;
